import { AxiosError } from "axios";
import { useIntl } from "react-intl";

import { logger } from "../../shared/services/SentryLogger.service";

const errorMessages = [
  // Telematics
  "telematics/account/not-exists",
  "telematics/account/update",
  "telematics/account/farm-missing-company-id",
  "telematics/drive-group/not-found",
  "telematics/driver/not-found",
  "telematics/drives/create/conflict",
  "telematics/drives/create/validation",
  "telematics/drives/delete",
  "telematics/drives/not-found",
  "telematics/drives/parcel-not-exists",
  "telematics/drives/part-error",
  "telematics/drives/separation/error",
  "telematics/drives/separation/interval-length",
  "telematics/drives/state",
  "telematics/drives/update/group-not-found",
  "telematics/drives/update/sowing-not-found",
  "telematics/drives/update/validation",
  "telematics/e2e/error",
  "telematics/economic-system/export",
  "telematics/equipment/catalogue-read-only",
  "telematics/equipment/duplicate-code",
  "telematics/equipment/not-found",
  "telematics/handwork/not-found",
  "telematics/handwork/validation",
  "telematics/machine/catalogue-read-only",
  "telematics/machine/duplicate-code",
  "telematics/machine/not-found",
  "telematics/driver/catalogue-read-only",
  "telematics/driver/duplicate-code",
  "telematics/operation/not-found",
  "telematics/position/dashboard-forbidden",
  "telematics/rule/not-found",
  "telematics/winfas/client-not-found",
  "telematics/winfas/client-required",
  "telematics/winfas/drive-key",
  "telematics/winfas/drive-key-update",
  // Agrodata
  "agrodata/file/already-confirmed",
  "agrodata/file/confirmation-failed",
  "agrodata/file/data/zone/ambiguous-attribute",
  "agrodata/file/data/zone/attribute-required",
  "agrodata/file/data/zone/invalid-attribute",
  "agrodata/file/invalid-payload",
  "agrodata/file/missing-season-id",
  "agrodata/file/not-found",
  "agrodata/file/s3-not-found",
  // IoT
  "iot/device/already-placed",
  "iot/device/placed-on-location",
  // Agroevidence
  "agroevidence/seed-applications/overlap",
  "agroevidence/seed-applications/validation",
  "agroevidence/parcels/valid-from/not-first-version",
  "agroevidence/parcels/valid-from/after-parcel-end",
  "agroevidence/parcels/valid-from/seed-applications-outside",
  "agroevidence/parcels/valid-from/too-far-in-past",
  "agroevidence/facilities/name-exists",
  "agroevidence/facilities/in-use",
  "agroevidence/facilities/in-use-attributes-locked",
  // Automation
  "automation/rule/user-not-on-farm",
  // Integrations
  "integrations/variodoc/already-exists",
  "integrations/variodoc/forbidden",
  "integrations/vidimte/already-exists",
  "integrations/vidimte/forbidden",
];

// "namespace/a/b/c" -> "namespace.error.a.b.c"
const codeToIntlId = (code: string): string => {
  const [namespace, ...rest] = code.split("/");
  return [namespace, "error", ...rest].join(".");
};

const resolveBackendErrorMessageId = (error: unknown): string | undefined => {
  if (!(error instanceof AxiosError)) return undefined;

  const backendCode = getBackendCode(error);
  if (!backendCode) return undefined;
  if (errorMessages.includes(backendCode)) return codeToIntlId(backendCode);

  logger.error(
    new Error(`Untranslated backend error code: ${backendCode}`, {
      cause: error,
    }),
  );
  return undefined;
};

// Two error envelopes are in play. Telematics, agrodata and integrations answer
// with `code` (integrations slash-prefixed: "/integrations/vidimte/forbidden");
// cf-rest services (iot, agroevidence, catalogues) answer with `error`, which
// holds the same kind of value, always slash-prefixed: "/iot/device/already-placed".
// That field also carries Spring's reason phrases ("Internal Server Error"),
// which are not codes at all — the leading slash is what tells them apart.
const getBackendCode = (
  error: AxiosError<{ code?: string; error?: string }>,
): string | undefined => {
  const data = error.response?.data;
  const code =
    data?.code ?? (data?.error?.startsWith("/") ? data.error : undefined);
  return code?.replace(/^\//, "");
};

// The cf-rest envelope carries a human-readable `message` next to the code. A translation that
// cannot say more than "validation failed" interpolates it as {message}, so the user learns which
// rule the backend refused on.
const getBackendMessage = (error: unknown): string => {
  if (!(error instanceof AxiosError)) return "";

  const data = error.response?.data as { message?: string } | undefined;
  return typeof data?.message === "string" ? data.message : "";
};

const useDisplayErrorMessage = () => {
  const intl = useIntl();
  const displayErrorMessage = (error: Error): string | undefined => {
    const messageId = resolveBackendErrorMessageId(error);
    return messageId ? intl.formatMessage({ id: messageId }) : undefined;
  };

  return { displayErrorMessage };
};

export {
  useDisplayErrorMessage,
  resolveBackendErrorMessageId,
  getBackendMessage,
};
